import { useCallback, useMemo, useState } from 'react'

import {
  GridCellEditStopReasons,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import type { ObjectPropertyDefinition } from 'types/graphql'
import { isUUID } from 'validator'

import { logger } from 'src/lib/logger'
import type { NativeObjectType } from 'src/lib/objects'

import Row from '../Row/Row'

import PropertyDisplay from './PropertyDisplay'
import PropertyEdit from './PropertyEdit'

const RenderPropertyEditCell = ({
  propertyDefinition,
  objectType,
  workspaceId,
  params,
  justify = 'flex-end',
}: {
  propertyDefinition: ObjectPropertyDefinition
  objectType: string
  workspaceId: string
  params: any
  justify?: 'flex-start' | 'flex-end'
}) => {
  const api = useGridApiContext()
  const propertyDefinitionId = propertyDefinition.id
  const isCustomProperty = isUUID(propertyDefinitionId)
  const fieldKey = isCustomProperty ? 'custom' : 'standard'
  const [manualValue, setManualValue] = useState(
    params.row.object?.properties?.[fieldKey]?.[propertyDefinitionId]
  )

  const handleUpdate = useCallback(
    (propertyValue) => {
      setManualValue(propertyValue)
      const rowUpdatePayload = [
        {
          ...params.row,
          [propertyDefinitionId]: propertyValue?.value,
          object: {
            ...params.row.object,
            properties: {
              ...(params.row.object?.properties || {}),
              [fieldKey]: {
                ...(params.row.object?.properties?.[fieldKey] || {}),
                [propertyDefinitionId]: propertyValue,
              },
            },
          },
        },
      ]

      logger.dev('RenderPropertyEditCell', {
        propertyValue,
        rowUpdatePayload,
      })
      api.current.updateRows(rowUpdatePayload)
    },
    [api, params, fieldKey, propertyDefinitionId]
  )

  const renderPropertyEditCellSx = useMemo(() => {
    return {
      height: '100%',
      width: '100%',
      px: '9px',
      justifyContent: justify,

      '& .picklist-options-row': {
        flexWrap: 'nowrap',
      },
    }
  }, [justify])

  return (
    <Row
      sx={renderPropertyEditCellSx}
      id={`${propertyDefinition.id}-edit-cell`}
    >
      <PropertyEdit
        propertyDefinition={propertyDefinition}
        objectId={params.row.id}
        objectType={objectType}
        workspaceId={workspaceId}
        value={manualValue}
        justify={justify}
        onUpdate={(value) => handleUpdate(value)}
        onClose={() => {
          // TODO: for no particular benefit known to me, we COULD take the onClose event and publish an accurate reason to the DataGridPremium API

          api.current.publishEvent('cellEditStop', {
            ...params,
            reason: GridCellEditStopReasons.cellFocusOut,
          })
        }}
        dispatch={(_) => {}}
        targetElementId={`${propertyDefinition.id}-edit-cell`}
      />

      <PropertyDisplay
        propertyDefinition={propertyDefinition}
        value={manualValue}
        workspaceId={workspaceId}
        objectId={params.row.id}
        objectType={objectType as NativeObjectType}
        justify={justify as 'flex-start' | 'flex-end'}
        object={params?.row?.object}
      />
    </Row>
  )
}

export default RenderPropertyEditCell
